import React from 'react';
import { FileTextIcon, UploadCloudIcon, XIcon } from 'lucide-react';
import type { CalculatorStepProps, ClaimType } from '../../types/claimCalculator';
import { YesNoField } from './YesNoField';
const inputClass = 'min-h-[46px] w-full rounded-input border border-ink/15 bg-canvas px-3.5 text-[13px] text-ink outline-none placeholder:text-slate/55 focus:border-forest focus:ring-2 focus:ring-signal/30';
const textareaClass = 'mt-1 w-full rounded-input border border-ink/15 bg-canvas px-3.5 py-3 text-[13px] text-ink outline-none focus:border-forest focus:ring-2 focus:ring-signal/30';
interface InjuryWorkStepProps extends CalculatorStepProps {
  claimType: ClaimType;
  files: File[];
  fileError: string;
  onFilesAdded: (files: FileList | null) => void;
  onFileRemoved: (index: number) => void;
}
export function InjuryWorkStep({
  claimType,
  data,
  update,
  files,
  fileError,
  onFilesAdded,
  onFileRemoved
}: InjuryWorkStepProps) {
  if (claimType === 'road') {
    return <div className="space-y-3" data-id="element-74" data-mp="1l8slz5y4qfw8">
        <p className="text-small font-semibold text-ink" data-id="element-75" data-mp="1l9u5lyt7oapk">Claim and work details</p>
        <YesNoField label="Have you lodged an online crash report" value={data.crashReportLodged} onChange={value => update('crashReportLodged', value)} data-id="element-76" data-mp="1gi2hduxj2h2l" />
        <DocumentUpload label="Please attach your online crash report" files={files} fileError={fileError} onFilesAdded={onFilesAdded} onFileRemoved={onFileRemoved} data-id="element-77" data-mp="1bgwhnd5g9tjk" />
        <YesNoField label="Has ICWA admitted the claim" value={data.icwaAdmitted} onChange={value => update('icwaAdmitted', value)} data-id="element-78" data-mp="1z8adht2t879a" />
        <div className="grid gap-3 sm:grid-cols-2" data-id="element-79" data-mp="1u8fpbxaf5vrx">
          <label className="text-[12px] font-semibold text-slate" data-id="element-80" data-mp="1paw7kneij0v8">
            Your occupation
            <input value={data.occupation} onChange={event => update('occupation', event.target.value)} className={`mt-1 ${inputClass}`} required data-id="element-81" data-mp="iubtelk9txk8" />
          </label>
          <label className="text-[12px] font-semibold text-slate" data-id="element-82" data-mp="du6070ycs301">
            Your employer's name
            <input value={data.employerName} onChange={event => update('employerName', event.target.value)} className={`mt-1 ${inputClass}`} required data-id="element-83" data-mp="31zbhzf5drmcy" />
          </label>
          <label className="text-[12px] font-semibold text-slate" data-id="element-84" data-mp="2s2gjuxd0rnz7">
            Your annual income
            <input value={data.annualIncome} onChange={event => update('annualIncome', event.target.value)} inputMode="decimal" className={`mt-1 ${inputClass}`} required data-id="element-85" data-mp="2x11l9040cdp8" />
          </label>
        </div>
        <WorkImpactField value={data.workImpact} onChange={value => update('workImpact', value)} data-id="element-86" data-mp="2n4yl1zspup4x" />
      </div>;
  }
  return <div className="space-y-3" data-id="element-87" data-mp="29axisw2k2jzt">
      <p className="text-small font-semibold text-ink" data-id="element-88" data-mp="2ebbuco3d8c2l">Injuries and claim details</p>
      <label className="block text-[12px] font-semibold text-slate" data-id="element-89" data-mp="1zeui7uncqtez">
        List all of your injuries
        <textarea value={data.injuries} onChange={event => update('injuries', event.target.value)} rows={3} className={textareaClass} required data-id="element-90" data-mp="24de4jw5uy77v" />
      </label>
      <YesNoField label="Have you had surgery" value={data.surgery} onChange={value => update('surgery', value)} data-id="element-91" data-mp="1ks5jvw4u6ma6" />
      {claimType === 'work' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-92" data-mp="1uql37cxmgr94">
          <YesNoField label="Have you lodged a workers' compensation claim" value={data.workersClaimLodged} onChange={value => update('workersClaimLodged', value)} data-id="element-93" data-mp="1unysj4ffpvmo" />
          <YesNoField label="Has the claim been admitted" value={data.claimAdmitted} onChange={value => update('claimAdmitted', value)} data-id="element-94" data-mp="23pf4hglwe5t0" />
          <YesNoField label="Are you currently receiving income compensation payments" value={data.receivingIncomePayments} onChange={value => update('receivingIncomePayments', value)} data-id="element-95" data-mp="1znbqolbrap4k" />
        </div>}
      {claimType === 'public' && <WorkImpactField value={data.workImpact} onChange={value => update('workImpact', value)} data-id="element-96" data-mp="2nfsab0b56p5z" />}
      {claimType === 'other' && <>
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-97" data-mp="2idt8huv1w0b5">
            <label className="text-[12px] font-semibold text-slate" data-id="element-98" data-mp="fs7dqnm8mbil">
              Your occupation
              <input value={data.occupation} onChange={event => update('occupation', event.target.value)} className={`mt-1 ${inputClass}`} data-id="element-99" data-mp="kqqyikp3hh9d" />
            </label>
            <label className="text-[12px] font-semibold text-slate" data-id="element-100" data-mp="uhljllqluat8">
              Your employer's name
              <input value={data.employerName} onChange={event => update('employerName', event.target.value)} className={`mt-1 ${inputClass}`} data-id="element-101" data-mp="3t00lm6ikp7km" />
            </label>
            <label className="text-[12px] font-semibold text-slate" data-id="element-102" data-mp="1v0wixh5wkvh">
              Your annual income
              <input value={data.annualIncome} onChange={event => update('annualIncome', event.target.value)} inputMode="decimal" className={`mt-1 ${inputClass}`} data-id="element-103" data-mp="6un7f09ipp14" />
            </label>
          </div>
          <WorkImpactField value={data.workImpact} onChange={value => update('workImpact', value)} data-id="element-104" data-mp="1i9saw6hh3rls" />
        </>}
    </div>;
}
interface WorkImpactFieldProps {
  value: CalculatorStepProps['data']['workImpact'];
  onChange: (value: CalculatorStepProps['data']['workImpact']) => void;
}
function WorkImpactField({
  value,
  onChange
}: WorkImpactFieldProps) {
  return <label className="block text-[12px] font-semibold text-slate" data-id="element-105" data-mp="2xwxd7eofat69">
      Has the injury stopped you from working?
      <select value={value} onChange={event => onChange(event.target.value as typeof value)} className={`mt-1 ${inputClass}`} required data-id="element-106" data-mp="2sxknmm6637zz">
        <option value="" data-id="element-107" data-mp="2jyh3rfz6eokf">Select one</option>
        <option value="Not at all" data-id="element-108" data-mp="2jxpbae0vl6yd">Not at all</option>
        <option value="Partially" data-id="element-109" data-mp="2eze2m6mbh2hq">Partially</option>
        <option value="I cannot work" data-id="element-110" data-mp="2f207fyhtp3w9">I cannot work</option>
      </select>
    </label>;
}
interface DocumentUploadProps {
  label: string;
  files: File[];
  fileError: string;
  onFilesAdded: (files: FileList | null) => void;
  onFileRemoved: (index: number) => void;
}
function DocumentUpload({
  label,
  files,
  fileError,
  onFilesAdded,
  onFileRemoved
}: DocumentUploadProps) {
  return <div data-id="element-111" data-mp="22od66ctv5g7a">
      <p className="text-[13px] font-semibold text-ink" data-id="element-112" data-mp="22ntq71nrx1vw">
        {label}<span className="font-normal text-slate" data-id="element-113" data-mp="27h6e19kmp4nl"> (optional)</span>
      </p>
      <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-input border border-dashed border-forest/35 bg-canvas px-4 py-4 text-center hover:border-forest focus-within:ring-2 focus-within:ring-signal/30" data-id="element-114" data-mp="1ss9y6qcbyncf">
        <UploadCloudIcon className="h-5 w-5 text-forest" aria-hidden="true" data-id="element-115" data-mp="2lgxm6togrqto" />
        <span className="text-[13px] font-semibold text-forest" data-id="element-116" data-mp="2qdxulb8sd5ny">Choose documents</span>
        <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="sr-only" onChange={event => {
        onFilesAdded(event.target.files);
        event.target.value = '';
      }} data-id="element-117" data-mp="2bgye0xvyi2hc" />
      </label>
      {fileError && <p role="alert" className="mt-2 text-[12px] font-medium text-signal" data-id="element-118" data-mp="152z39phnvn67">{fileError}</p>}
      {files.length > 0 && <ul className="mt-2 space-y-2" data-id="element-119" data-mp="3j842wl2k2wgi">
          {files.map((file, index) => <li key={`${file.name}-${file.lastModified}`} className="flex items-center gap-2 rounded-input bg-warm-tint px-3 py-2 text-[12px] text-ink" data-id="element-120" data-mp="3e9j3uoz1iitn" data-mpi={index}>
              <FileTextIcon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-121" data-mp="3o8s1psx10hla" />
              <span className="min-w-0 flex-1 truncate" data-id="element-122" data-mp="3o6xhyhxhqvis">{file.name}</span>
              <button type="button" onClick={() => onFileRemoved(index)} className="rounded p-1 text-slate hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal" aria-label={`Remove ${file.name}`} data-id="element-123" data-mp="30jfkc5rjp37w">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-124" data-mp="34kso8ab2uwc6" />
              </button>
            </li>)}
        </ul>}
    </div>;
}